import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@/shared/lib/cn';
import { buttonClassName } from '@/shared/ui/buttonStyles';
import {
  abrirNaCamadaDeCima,
  posicionarJunto,
  temPopover,
} from '@/shared/ui/flutuante';

export interface AcaoDoMenu {
  rotulo: string;
  onSelect: () => void;
  perigo?: boolean;
}

/**
 * Menu de acoes da linha, como o botao de tres pontos do OnPoint.
 *
 * O menu fecha pela tecla de escape e pelo clique fora, e cada item e um botao com
 * papel de item de menu: quem usa teclado ou leitor de tela chega a ele como chega
 * ao resto da tela.
 *
 * O menu fica por cima da tela (`flutuante.ts`), alinhado pelo fim do botao: a coluna
 * de acoes e a ultima da tabela, e o menu absoluto dentro dela passava da borda,
 * criava a barra de rolagem da tabela e era cortado nas ultimas linhas. Rolar ou
 * redimensionar a janela fecha o menu.
 */
export function MenuDeAcoes({
  titulo,
  acoes,
}: {
  titulo: string;
  acoes: AcaoDoMenu[];
}) {
  const [aberto, setAberto] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const botao = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLUListElement>(null);
  const idDoMenu = useId();

  useEffect(() => {
    if (!aberto) return;
    const fora = (evento: MouseEvent) => {
      if (!raiz.current?.contains(evento.target as Node)) setAberto(false);
    };
    const tecla = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAberto(false);
    };
    const fechar = () => setAberto(false);
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', tecla);
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('resize', fechar);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', tecla);
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('resize', fechar);
    };
  }, [aberto]);

  // Antes de pintar: sobe o menu para a camada de cima e o coloca junto do botao.
  useLayoutEffect(() => {
    const elemento = menu.current;
    const ancora = botao.current;
    if (!aberto || !elemento || !ancora) return;
    abrirNaCamadaDeCima(elemento);
    posicionarJunto(elemento, ancora, 'fim');
  }, [aberto]);

  if (acoes.length === 0) return null;

  return (
    <div className="relative" ref={raiz} onClick={(evento) => evento.stopPropagation()}>
      <button
        ref={botao}
        type="button"
        aria-haspopup="menu"
        aria-expanded={aberto}
        aria-controls={aberto ? idDoMenu : undefined}
        aria-label={titulo}
        onClick={() => setAberto((atual) => !atual)}
        className={buttonClassName({
          variant: aberto ? 'soft' : 'secondary',
          size: 'sm',
        })}
      >
        Acoes
        <span aria-hidden="true">...</span>
      </button>
      {aberto ? (
        <ul
          ref={menu}
          id={idDoMenu}
          role="menu"
          aria-label={titulo}
          popover={temPopover ? 'manual' : undefined}
          className="surface-card fixed inset-auto z-30 m-0 w-56 p-1 shadow-(--shadow-lg)"
        >
          {acoes.map((acao) => (
            <li key={acao.rotulo} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAberto(false);
                  acao.onSelect();
                }}
                className={cn(
                  'w-full rounded-lg px-3 py-2 text-left text-sm transition-colors duration-(--duration-fast)',
                  acao.perigo
                    ? 'text-danger-700 hover:bg-danger-50'
                    : 'text-ink-800 hover:bg-brand-50 hover:text-brand-700',
                )}
              >
                {acao.rotulo}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
